import { useMemo } from "react";
import { useAuthStore } from "../stores/auth-store";
import type { Permission } from "../types/permissions";

/**
 * Hook for checking the current user's permissions.
 *
 * Permissions are sourced from the auth store, populated at login and refreshed
 * via /auth/me. Backed by the BE permission catalog at
 * dahling/api/src/common/permissions/permissions.ts.
 *
 * @example
 * const { hasPermission, hasAny } = usePermissions();
 * if (!hasPermission(Permissions.CANDIDATE_SEARCH)) return null;
 *
 * @example
 * const { hasAny } = usePermissions();
 * if (!hasAny(Permissions.PROJECT_CREATE, Permissions.PROJECT_EDIT)) return null;
 */
export function usePermissions() {
  const permissions = useAuthStore((s) => s.permissions);

  return useMemo(() => {
    const set = new Set(permissions);
    return {
      permissions,
      hasPermission: (p: Permission) => set.has(p),
      hasAny: (...required: Permission[]) => required.some((p) => set.has(p)),
      hasAll: (...required: Permission[]) => required.every((p) => set.has(p)),
    };
  }, [permissions]);
}
